import React, { useEffect, useState } from 'react';
import { api, formatPhoneInput } from '../api';
import { AppSettings, TableRecord } from '../types';

interface Props {
  tableId: string;
}

const DEFAULT_SETTINGS: AppSettings = {
  venueTitle: '공인대 주점',
  maxTableNumber: 65,
  defaultDurationMinutes: 120,
};

function formatCountdown(ms: number) {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const TimerPage = ({ tableId }: Props) => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [table, setTable] = useState<TableRecord | null>(null);
  const [tick, setTick] = useState<number>(Date.now());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTick(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadTable() {
      try {
        const response = await api.getPublicTable(tableId);
        if (cancelled) return;
        setSettings(response.settings);
        setTable(response.table);
        setError('');
        document.title = `${response.settings.venueTitle} 이용 시간`;
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : '테이블 정보를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadTable();
    const pollId = window.setInterval(loadTable, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, [tableId]);

  const remainingTime = table ? table.endTime - tick : 0;
  const isExpired = remainingTime <= 0;

  if (loading) {
    return <div className="empty-state">이용 정보를 불러오는 중입니다.</div>;
  }

  if (error || !table) {
    return (
      <div className="empty-state">
        <h2>테이블 정보를 찾을 수 없습니다.</h2>
        <p>{error || '주소가 올바른지 확인해주세요.'}</p>
        <button type="button" onClick={() => { window.location.hash = ''; }}>
          처음으로
        </button>
      </div>
    );
  }

  return (
    <div className="timer-page">
      <header className="page-header page-header--compact">
        <span className="page-badge">www.공인대주점.com</span>
        <h1>{settings.venueTitle}</h1>
        <p>{table.name}님 이용 시간 안내</p>
      </header>

      <section className="timer-card">
        <p className="timer-label">남은 이용 시간</p>
        <div className={`timer-value ${isExpired ? 'timer-value--expired' : ''}`}>
          {formatCountdown(remainingTime)}
        </div>
        <div className="timer-meta">
          <span>테이블 {table.tableNumber}번</span>
          <span>{table.partySize}명</span>
          <span>{formatPhoneInput(table.phone)}</span>
        </div>
      </section>

      <section className="timer-notice">
        <strong>{isExpired ? '이용 시간이 종료되었습니다.' : '시간 확인을 위해 이 화면을 유지해주세요.'}</strong>
        <p>다른 창을 사용하려면 링크를 새 탭에서 열어도 됩니다. 이용 종료 후에는 다음 손님을 위해 자리를 비워주세요.</p>
      </section>

      <button type="button" className="ghost-button" onClick={() => { window.location.hash = ''; }}>
        처음으로 돌아가기
      </button>
    </div>
  );
};

export default TimerPage;
